import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NsKey } from 'enums/NsKey';
import { Button } from 'components/ui/Button';
import { Checkbox } from 'components/forms/fragments/Checkbox';
import { GenericLoading, Sizes } from 'components/ui/Loader';
import { ExternalLink, FileText } from 'lucide-react';

interface AerasReadingStepProps {
  aerasFileUrl: string;
  aerasFileMimeType?: string | null;
  phoneMasked?: string | null;
  onSubmit: () => Promise<void>;
  isSubmitting: boolean;
  errorMessage?: string | null;
}

const AerasReadingStep: FC<AerasReadingStepProps> = ({
  aerasFileUrl,
  aerasFileMimeType,
  phoneMasked,
  onSubmit,
  isSubmitting,
  errorMessage
}) => {
  const { t } = useTranslation(NsKey.MncapMedicalValidation);
  const [isChecked, setIsChecked] = useState(false);
  const isPdf = !aerasFileMimeType || aerasFileMimeType === 'application/pdf';

  return (
    <div className="w-full max-w-4xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col">
      <header className="px-6 py-5 border-b border-gray-200 bg-primary/5">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 bg-primary/10 rounded-full p-2">
            <FileText className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              {t('MNCAP_MEDICAL_VALIDATION.AERAS.TITLE')}
            </h1>
            <p className="text-sm text-gray-600 mt-0.5">
              {t('MNCAP_MEDICAL_VALIDATION.AERAS.SUBTITLE')}
            </p>
          </div>
        </div>
      </header>

      <div className="flex-1 p-4 sm:p-6">
        <div className="h-[55vh] bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
          {isPdf ? (
            <iframe
              src={`${aerasFileUrl}#toolbar=0&navpanes=0`}
              title="AERAS document"
              className="w-full h-full"
              style={{ border: 'none' }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <img
                src={aerasFileUrl}
                alt="AERAS document"
                className="max-w-full max-h-full object-contain"
              />
            </div>
          )}
        </div>
      </div>

      <footer className="px-6 py-5 border-t border-gray-200 bg-gray-50 space-y-4">
        <div className="flex items-start gap-3 flex-wrap">
          <label className="flex items-start gap-3 cursor-pointer flex-1 min-w-0">
            <Checkbox
              name="aerasRead"
              checked={isChecked}
              onChange={(checked: boolean) => setIsChecked(checked)}
              disabled={isSubmitting}
              className="mt-0.5 flex-none"
            />
            <span className="text-sm text-gray-800 select-none">
              {t('MNCAP_MEDICAL_VALIDATION.AERAS.CHECKBOX_LABEL')}
            </span>
          </label>
          <a
            href={aerasFileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 underline underline-offset-2 font-medium"
          >
            <ExternalLink className="w-4 h-4" />
            {t('MNCAP_MEDICAL_VALIDATION.AERAS.OPEN_IN_NEW_TAB')}
          </a>
        </div>

        {phoneMasked && (
          <p className="text-sm text-gray-600">
            {t('MNCAP_MEDICAL_VALIDATION.AERAS.PHONE_INFO', { phone: phoneMasked })}
          </p>
        )}

        {errorMessage && (
          <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-3 text-sm text-red-700">
            {errorMessage}
          </div>
        )}

        <div className="flex justify-end">
          <Button
            color="primary"
            disabled={!isChecked || isSubmitting}
            onClick={() => onSubmit()}
          >
            {isSubmitting ? (
              <>
                <GenericLoading size={Sizes.sm} isInline loading />
                <span className="ml-2">{t('MNCAP_MEDICAL_VALIDATION.SMS.SENDING')}</span>
              </>
            ) : (
              t('MNCAP_MEDICAL_VALIDATION.AERAS.SUBMIT')
            )}
          </Button>
        </div>
      </footer>
    </div>
  );
};

export default AerasReadingStep;
